import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { obtenerAdminAplicacion } from '../../services/adminAplicaciones';
import AppShell from '../../components/layout/AppShell';
import StatusBadge from '../../components/ui/StatusBadge';

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return fechaaplicacion;
  }
}

function formatFechaHora(valor) {
  if (!valor) return '—';
  try {
    return new Date(valor).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return valor;
  }
}

function formatHora(hora) {
  if (!hora) return '—';
  return hora.slice(0, 5);
}

function OrigenBadge({ origen }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-500">
      {origen === 'webapp' ? 'App Campo' : 'WhatsApp'}
    </span>
  );
}

function AdminAplicacionDetailPage() {
  const { id } = useParams();

  const [aplicacion, setAplicacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    obtenerAdminAplicacion(id).then((result) => {
      if (!active) return;
      if (result.ok) {
        setAplicacion(result.aplicacion);
      } else {
        setError(result.message);
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <AppShell title="Aplicación" backTo="/admin/aplicaciones">
        <p className="text-center text-sm text-gray-500">Cargando...</p>
      </AppShell>
    );
  }

  if (error || !aplicacion) {
    return (
      <AppShell title="Aplicación" backTo="/admin/aplicaciones">
        <p className="text-center text-sm text-red-600">{error || 'Aplicación no encontrada.'}</p>
      </AppShell>
    );
  }

  const productos = aplicacion.productos ?? [];
  const esAnulada = aplicacion.estado === 'ANULADA';

  return (
    <AppShell title="Aplicación" backTo="/admin/aplicaciones">
      <div className="flex flex-col gap-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Aplicación #{aplicacion.id}</h2>
              <div className="mt-1 flex items-center gap-2">
                <OrigenBadge origen={aplicacion.origen} />
              </div>
            </div>
            <StatusBadge estado={aplicacion.estado} />
          </div>

          <p className="mt-5 text-sm font-medium text-gray-500">Ingresada por</p>
          <div className="mt-2 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-gray-500">Usuario</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.encargado_nombre || '—'}</dd>
              {aplicacion.encargado_email && <dd className="text-gray-500">{aplicacion.encargado_email}</dd>}
            </div>
            <div>
              <dt className="font-medium text-gray-500">Productor</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.productor_nombre || '—'}</dd>
            </div>
          </div>
        </div>

        {esAnulada && (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <p className="font-medium text-gray-900">Aplicación anulada</p>
            {aplicacion.motivo_anulacion && (
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-500">Motivo:</span> {aplicacion.motivo_anulacion}
              </p>
            )}
            <p className="mt-1 text-sm text-gray-600">
              <span className="font-medium text-gray-500">Fecha anulación:</span>{' '}
              {formatFechaHora(aplicacion.fecha_anulacion)}
            </p>
            {aplicacion.encargado_anulacion_nombre && (
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-medium text-gray-500">Anulada por:</span>{' '}
                {aplicacion.encargado_anulacion_nombre}
              </p>
            )}
          </div>
        )}

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <p className="text-sm font-medium text-gray-500">Información general</p>
          <dl className="mt-2 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="font-medium text-gray-500">Fecha</dt>
              <dd className="mt-1 text-gray-900">{formatFecha(aplicacion.fechaaplicacion)}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Huerto</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.huerto || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Cuartel</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.cuartel || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Superficie (ha)</dt>
              <dd className="mt-1 text-gray-900">{aplicacion.ha ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Hora inicio</dt>
              <dd className="mt-1 text-gray-900">{formatHora(aplicacion.horainicio)}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Hora término</dt>
              <dd className="mt-1 text-gray-900">{formatHora(aplicacion.horafin)}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h3 className="font-semibold text-gray-900">Productos</h3>

          {productos.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">Sin productos registrados.</p>
          ) : (
            <>
              <div className="mt-4 hidden overflow-hidden rounded-xl border border-gray-100 sm:block">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Producto</th>
                      <th className="px-4 py-2 font-medium">Dosis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {productos.map((producto) => (
                      <tr key={producto.id}>
                        <td className="px-4 py-2 text-gray-900">{producto.nombrecomercial || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{producto.dosis || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-col gap-4 sm:hidden">
                {productos.map((producto, index) => (
                  <div key={producto.id} className={index > 0 ? 'border-t border-gray-100 pt-4' : ''}>
                    <p className="font-medium text-gray-900">{producto.nombrecomercial || '—'}</p>
                    {producto.dosis && (
                      <p className="mt-1 text-sm text-gray-600">
                        <span className="font-medium text-gray-500">Dosis:</span> {producto.dosis}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <p className="text-sm font-medium text-gray-500">Auditoría</p>
          <dl className="mt-2 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-gray-500">Registrada</dt>
              <dd className="mt-1 text-gray-900">{formatFechaHora(aplicacion.created_at)}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Última actualización</dt>
              <dd className="mt-1 text-gray-900">{formatFechaHora(aplicacion.updated_at)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </AppShell>
  );
}

export default AdminAplicacionDetailPage;
